import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const MotionPaper = motion(Paper);

const EmailConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const email = searchParams.get('email') || '';

  const handleResendEmail = async () => {
    if (!email) {
      toast.error('Email address not found. Please try registering again.');
      return;
    }

    try {
      setIsResending(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      setResendSuccess(true);
      toast.success('Confirmation email sent!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend confirmation email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <MotionPaper
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          elevation={3}
          sx={{
            p: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <EmailIcon
              sx={{
                fontSize: 80,
                color: 'primary.main',
                mb: 3,
              }}
            />
          </motion.div>

          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Check Your Email! 📧
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px' }}>
            We've sent a confirmation email to <strong>{email}</strong>
          </Typography>

          <Alert severity="info" sx={{ mb: 4, textAlign: 'left', maxWidth: '600px' }}>
            <Typography variant="body1" gutterBottom>
              <strong>To complete your registration:</strong>
            </Typography>
            <Typography component="div" variant="body2">
              <Box component="ol" sx={{ pl: 2, mb: 0 }}>
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the confirmation link in the email</li>
                <li>Return here to sign in and start your journey!</li>
              </Box>
            </Typography>
          </Alert>

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 3, maxWidth: '600px' }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              Confirmation email sent successfully!
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={isResending ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleResendEmail}
              disabled={isResending || !email}
              sx={{ minWidth: '200px' }}
            >
              {isResending ? 'Sending...' : 'Resend Email'}
            </Button>

            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/login"
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                },
                minWidth: '200px',
              }}
            >
              Go to Sign In
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px' }}>
            <strong>Didn't receive the email?</strong><br />
            Check your spam folder or try resending. If you're still having trouble, 
            contact our support team for assistance.
          </Typography>

          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Need to use a different email address?{' '}
              <Button
                component={Link}
                to="/register"
                variant="text"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Register again
              </Button>
            </Typography>
          </Box>
        </MotionPaper>
      </Box>
    </Container>
  );
};

export default EmailConfirmation; 